/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { SubTitleLayout, SubTitleGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import { tb } from 'components/layouts/Table';
import {
  confirmSimilarModel,
  findReviewModel,
  findSimilarModelsWithScoreList,
  saveSimilarModelsWithScore,
} from 'apis/md/MdProject';
import { MdIvesMdlSmly, MdIvesMdlSmlyScor } from 'models/md/MdIves';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import _ from 'lodash';
import useEvent from 'react-use-event-hook';
import { useCommonModal } from 'hooks/useCommonModal';
import { ComboBox } from 'components/selects/ComboBox';

export type SmlyPopupProps = 'READ' | 'WRITE' | null;
type Props = {
  open: boolean;
  close: () => void;
  condition: {
    gbn: SmlyPopupProps;
    elmPrjId: string;
    ivesMdlId: string;
  };
  callbackFn: (model: MdIvesMdlSmly) => void;
};

/**
 * 유사 모델 조회 팝업
 */
const ModelSimilityPopup = ({ open, close, callbackFn, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();

  const condition = props.condition;
  const [rowData, setRowData] = useState<MdIvesMdlSmly[]>([]);
  const [cfmModel, setCfmModel] = useState<MdIvesMdlSmly>({});
  const [selectedModel, setSelectedModel] = useState<MdIvesMdlSmly>({});
  const [reviewMdoel, setReviewMdoel] = useState<MdIvesMdlSmlyScor[]>([]);
  const [code, setCode] = useState<any>();

  const etcList = useMemo(() => {
    return rowData
      .filter((o, i) => i >= 3)
      .map((o, i) => ({
        ...o,
        cmnCd: o.mdlId,
        cmnCdNm: `${i + 4}. ${o.mdlNm} ${o.cfmYn === 'Y' ? '(확정)' : ''}`,
      }));
  }, [rowData]);

  const btnSave = useEvent(() => {
    if (_.isEmpty(cfmModel)) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 모델이 없습니다.', '선택된 모델이 없습니다.'),
      });
      return;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.저장하시겠습니까?`, `저장하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        confirmSimilarModel(cfmModel)
          .then((res) => {
            if (res.successOrNot === 'Y') {
              callbackFn(cfmModel);
              close();
            } else {
              openMessageBar({
                type: 'error',
                content: t('com.msg.저장에 실패하였습니다.', '저장에 실패하였습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  });

  const getCommonCodes = async () => {
    openLoading(true);
    const mdMdlSpecCtgr: Code[] = await getCommonCodeNames('MD_MDL_SPEC_CTGR');
    const mdlSpecCd: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'MDL_SPEC_CD',
      optValCtn5: 'Y',
    });

    const groupingMdlSpecCd = (mdlSpecCd || []).reduce((acc, cur) => {
      if (cur.optValCtn1) {
        if (!acc[cur.optValCtn1]) {
          acc[cur.optValCtn1] = [];
        }
        acc[cur.optValCtn1].push(cur);
      }
      return acc;
    }, {});

    setCode({
      mdMdlSpecCtgr: mdMdlSpecCtgr,
      mdlSpecCd: mdlSpecCd,
      groupingMdlSpecCd: groupingMdlSpecCd,
    });

    // 유사모델 산출
    if (props.condition.gbn === 'WRITE') {
      await saveSimilarModelsWithScore(props.condition);
    }

    // 검사모델 조회
    const reviewList = await findReviewModel(condition);
    setReviewMdoel(reviewList);

    const scoreList = await findSimilarModelsWithScoreList(condition);
    setRowData(scoreList);
    if (scoreList.length) {
      setCfmModel({
        ...scoreList.find((o) => o.cfmYn === 'Y'),
      });

      // ETC 목록에 이전 유사모델이 있는 경우
      if (scoreList.findIndex((o) => o.cfmYn === 'P') >= 3) {
        setSelectedModel({
          ...scoreList.find((o, i) => o.cfmYn === 'P'),
        });
      } else {
        setSelectedModel({
          ...scoreList.find((o, i) => o.cfmYn === 'Y'),
        });
      }
    }
    openLoading(false);
  };

  const btnSelectModel = (o) => {
    setCfmModel(o);
  };

  const btnSelectEtcModel = () => {
    if (!_.isEmpty(selectedModel)) {
      setCfmModel(selectedModel);
    }
  };

  const onChangeEtcModel = (value) => {
    const selectedItem = rowData.find((o) => o.mdlId === value);
    setSelectedModel(selectedItem || {});
    if (condition.gbn === 'WRITE') {
      if (etcList.some((o) => o.mdlId === cfmModel.mdlId)) setCfmModel(selectedItem || {});
    }
  };

  useEffect(() => {
    getCommonCodes();
  }, []);

  const dialogButtons = useMemo(() => {
    if (condition.gbn === 'WRITE')
      return [
        <Button
          key={'confirm'}
          css={IconButton.button}
          className="confirm"
          onClick={btnSave}
          disableRipple
        >
          {t('com.button.유사모델 저장', '유사모델 저장')}
        </Button>,
      ];
    else return [];
  }, []);

  return (
    <CustomDialog
      title={String(t('md.label.유사모델 산출', '유사모델 산출'))}
      size={'xl'}
      open={open}
      onClose={close}
      buttons={dialogButtons}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>
            {t('md.label.검토모델', '검토모델')} : {reviewMdoel[0]?.mdlNm || ''}
          </h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <TableContainer css={tb.tableCustom} className="flex">
        <Table style={{ flex: 2 }}>
          <TableHead>
            <TableRow sx={{ height: 37 * 3 }}>
              <TableCell colSpan={2}>{String(t('md.label.구분', '구분'))}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(code?.mdlSpecCd || []).map((o, i) => {
              const group = code?.groupingMdlSpecCd?.[o.optValCtn1] || [];
              const isGroup = group.length > 0;
              const isFirstGroup = isGroup && group[0].cmnCd === o.cmnCd;
              let groupNm = '';
              if (isGroup) {
                const ctg = (code?.mdMdlSpecCtgr || []).filter((o) => o.cmnCd === o.optValCtn1);
                groupNm = (ctg || []).length ? ctg[0].cmnCdNm : '';
              }

              return (
                <>
                  {isGroup ? (
                    isFirstGroup ? (
                      <TableRow>
                        <TableCell className="HdBg" rowSpan={group.length}>
                          {groupNm || o.optValCtn1}
                        </TableCell>
                        <TableCell className="HdBg">{o.cmnCdNm}</TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell className="HdBg">{o.cmnCdNm}</TableCell>
                      </TableRow>
                    )
                  ) : (
                    <TableRow>
                      <TableCell className="HdBg" colSpan={2}>
                        {o.cmnCdNm}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
            <TableRow>
              <TableCell className="HdBg" colSpan={2}>
                {String(t('md.label.유사도', '유사도'))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="HdBg" colSpan={2}>
                {String(t('md.label.투자항목 적용 가능 개수', '투자항목 적용 가능 개수'))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table style={{ flex: 1 }} className="rvw-mdl">
          <TableHead>
            <TableRow sx={{ height: 37 * 2 }}>
              <TableCell>{String(t('md.label.검토모델', '검토모델'))}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Spec</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(reviewMdoel || []).map((so, si) => {
              return (
                <TableRow key={`${so.mdlNm}_${so.specAtcId}`}>
                  <TableCell>{so.specValCdNm || so.specValCtn}</TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {(rowData || []).length ? (
          <>
            {(rowData || [])
              .filter((o, i) => i < 3)
              .map((o, i) => (
                <Table
                  key={o.mdlId}
                  style={{ flex: 3 }}
                  className={o.mdlId === cfmModel.mdlId ? 'cfm-mdl' : ''}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Radio
                          name="selectedSmlyModel"
                          sx={{ width: 30, height: 30 }}
                          value={o.mdlId}
                          checked={o.mdlId === cfmModel.mdlId}
                          disabled={condition.gbn === 'READ'}
                          onClick={() => btnSelectModel(o)}
                        />
                        Rank {i + 1}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3}>{o.mdlNm}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Spec</TableCell>
                      <TableCell>Gap</TableCell>
                      <TableCell>{String(t('md.label.점수', '점수'))}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(o.scores || []).map((so, si) => {
                      return (
                        <TableRow key={`${o.mdlNm}_${so.specAtcId}`}>
                          <TableCell>{so.specValCdNm || so.specValCtn}</TableCell>
                          <TableCell>{so.difValCtnNm || so.difValCtn}</TableCell>
                          <TableCell>{so.cvcgScor}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell>{o.totCvcgScor}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3}>
                        {o.atcAplyCnt}
                        {String(t('md.label.건', '건'))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ))}
            <Table
              style={{ flex: 3 }}
              className={
                etcList.some((o) => o.mdlId === cfmModel.mdlId) &&
                cfmModel.mdlId === selectedModel.mdlId
                  ? 'cfm-mdl'
                  : ''
              }
            >
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>
                    <Radio
                      name="selectedSmlyModel"
                      sx={{ width: 30, height: 30 }}
                      value={''}
                      checked={etcList.some((o) => _.isEqual(o.mdlId, cfmModel.mdlId))}
                      readOnly
                      disabled={condition.gbn === 'READ'}
                      onChange={btnSelectEtcModel}
                    />
                    ETC
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={3}>
                    <ComboBox
                      classNameWrap="grow text-left"
                      options={etcList}
                      placeholder={String(t('com.label.선택', '선택'))}
                      defaultValue={selectedModel.mdlId}
                      onChange={onChangeEtcModel}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Spec</TableCell>
                  <TableCell>Gap</TableCell>
                  <TableCell>{String(t('md.label.점수', '점수'))}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {etcList.some((o) => _.isEqual(o.mdlId, selectedModel.mdlId)) && (
                  <>
                    {(selectedModel.scores || []).map((so, si) => {
                      return (
                        <TableRow key={`${selectedModel.mdlNm}_${so.specAtcId}`}>
                          <TableCell>{so.specValCdNm || so.specValCtn}</TableCell>
                          <TableCell>{so.difValCtnNm || so.difValCtn}</TableCell>
                          <TableCell>{so.cvcgScor}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell>{selectedModel.totCvcgScor}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3}>
                        {selectedModel.atcAplyCnt}
                        {String(t('md.label.건', '건'))}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </>
        ) : (
          <>
            <Table style={{ flex: 7 }}>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={7}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array((code?.mdlSpecCd || []).length).map((o, i) => {
                  return (
                    <TableRow key={i}>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </TableContainer>
    </CustomDialog>
  );
};

export default ModelSimilityPopup;
